import type { Journey } from '@epilot/journey-logic-commons/src/types/next'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'

import { getJourneyDesign } from '../services/design-service'
import { designChanged } from '../store/actions/designChanged'
import { setAppError } from '../store/actions/setAppError'
import { selectJourney, selectLauncherJourney } from '../store/selectors/config'
import { selectHasDesign } from '../store/selectors/runtime'
import { APP_ERROR } from '../store/types/enums'

import { useAppStoreSelector } from './useAppStoreSelector'

const getDesignQueryKey = (journey: Journey | undefined) => {
  return ['design', journey?.organizationId, journey?.settings.designId]
}

export const useDesignLoader = () => {
  const launcherJourney = useAppStoreSelector(selectLauncherJourney)
  const selectedJourney = useAppStoreSelector(selectJourney)
  const hasDesign = useAppStoreSelector(selectHasDesign)

  const launcherQuery = useQuery({
    queryKey: getDesignQueryKey(launcherJourney),
    queryFn: ({ signal }) => {
      return getJourneyDesign(
        launcherJourney!.organizationId,
        launcherJourney!.settings.designId,
        signal
      )
    },
    enabled: Boolean(launcherJourney?.settings.designId)
  })

  useEffect(() => {
    if (launcherQuery.data) {
      const castedData = launcherQuery.data as any

      designChanged({
        journeyId: launcherJourney!.id,
        design: {
          id: castedData?.design?.id,
          logoUrl: castedData?.design?.style?.logo?.main?.url,
          theme: castedData?.theme,
          designTokens: castedData?.design?.journey_design_tokens
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [launcherJourney?.id, launcherQuery.data])

  const selectedQuery = useQuery({
    queryKey: getDesignQueryKey(selectedJourney),
    queryFn: ({ signal }) => {
      return getJourneyDesign(
        selectedJourney!.organizationId,
        selectedJourney!.settings.designId,
        signal
      )
    },
    enabled: Boolean(selectedJourney?.settings.designId)
  })

  useEffect(() => {
    if (selectedQuery.data) {
      const castedData = selectedQuery.data as any

      designChanged({
        journeyId: selectedJourney!.id,
        design: {
          id: castedData?.design?.id,
          logoUrl: castedData?.design?.style?.logo?.main?.url,
          theme: castedData?.theme,
          designTokens: castedData?.design?.journey_design_tokens
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJourney?.id, selectedQuery.data])

  useEffect(() => {
    if (selectedQuery.isError && !hasDesign) {
      setAppError({
        error: APP_ERROR.JOURNEY_DESIGN_NOT_FOUND,
        reason: 'Unable to fetch the design for the given Journey'
      })
    }
  }, [selectedQuery.isError, hasDesign])
}
